// The daily horse race at Five Boroughs Park. Bets close when the race goes off at 12:30;
// the server runs the race and this page replays it from the recorded checkpoints.
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration } from '../state.tsx';
import { Loading, Panel, useLoad } from '../components/ui.tsx';
import { CasinoNav } from './Casino.tsx';
import { play, type SoundHandle } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';
import { getSettings } from '../fx/settings.ts';

export function Silks({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="silks">
      <path d="M4 6 l6 -4 l6 4 v12 h-12z" fill={color} stroke="#000" strokeWidth="1" />
      <path d="M4 6 l6 4 l6 -4" stroke="#fff" strokeWidth="1.5" fill="none" opacity="0.8" />
    </svg>
  );
}

/** An ink silhouette of a racehorse and jockey (faces right). */
function HorseInk() {
  return (
    <svg width="28" height="20" viewBox="0 0 28 20" aria-hidden="true">
      <path d="M3 9 q2 -4 8 -4 h7 l3 -3 l2 1 l-1 2 l3 2 l-1 2 l-3 -1 l-2 2 q0 3 -2 4 l2 5 h-2 l-2 -4 h-6 l-2 4 h-2 l1 -5 q-3 -1 -3 -4 l-3 1z" fill="#1d1a16" />
      <path d="M12 5 l1 -3 l3 -1 l1 2 l-2 2z" fill="#b3261e" />
      <circle cx="15" cy="1.5" r="1.2" fill="#1d1a16" />
    </svg>
  );
}

const when = (t: number) => new Date(t).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });

export function RacesPage() {
  const { me, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/races'), [], 15_000);
  const [horse, setHorse] = useState<number | null>(null);
  const [kind, setKind] = useState<'win' | 'place'>('win');
  const [amount, setAmount] = useState('');
  const [, tick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(h);
  }, []);
  if (!data) return <Loading />;
  const offset = data.serverNow - Date.now();
  const toPost = data.open.postTime - (Date.now() + offset);
  if (toPost < -2000 && toPost > -60_000) setTimeout(reload, 3000);
  const amt = Math.floor(Number(amount.replace(/[^0-9]/g, '')));
  const h = horse !== null ? data.open.horses[horse] : null;
  const staked = data.open.myBets.reduce((s: number, b: any) => s + b.amount, 0);
  const bet = async () => {
    if (horse === null) return;
    play('chips');
    await act('/api/races/bet', { horse, kind, amount: amt }, { success: () => ({ kind: 'good', text: <>You put <span className="money">{money(amt)}</span> on <b>{h.name}</b> to {kind}. Good luck!</> }) });
    reload();
  };
  return (
    <div>
      <CasinoNav game="races" />
      <div className="race-header">
        <div>
          <div className="race-track">{data.track}</div>
          <div className="small muted">One race a day. Post time {when(data.open.postTime)}.</div>
        </div>
        <div className="race-countdown">{toPost > 0 ? <>Off in <b>{duration(toPost)}</b></> : <b>They’re off!</b>}</div>
      </div>

      {data.last && <Replay race={data.last} />}

      <Panel title={`Today’s card · ${fmt(data.open.bettors)} ${data.open.bettors === 1 ? 'bettor' : 'bettors'} so far`}>
        <table className="list stack race-card">
          <thead><tr><th>#</th><th>Horse</th><th>Form</th><th>Win odds</th><th>Place pays</th><th></th></tr></thead>
          <tbody>
            {data.open.horses.map((x: any, i: number) => (
              <tr key={i} className={horse === i ? 'selected' : ''} onClick={() => setHorse(i)} style={{ cursor: 'pointer' }}>
                <td>{i + 1}</td>
                <td><Silks color={x.silks} /> <b>{x.name}</b></td>
                <td className="mono">{x.form}</td>
                <td className="gold">{x.winLabel}</td>
                <td>×{x.placeOdds.toFixed(2)}</td>
                <td><button className={`btn ${horse === i ? 'green' : ''}`} onClick={() => setHorse(i)}>{horse === i ? 'Picked' : 'Pick'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="small dim">“Form” is each horse’s last five finishes (1 = won). A <b>win</b> bet pays if your horse finishes first; a <b>place</b> bet pays (less) if it finishes first or second.</div>
      </Panel>

      <div className="panel"><div className="panel-body">
        {toPost <= 0 ? <div className="muted">Betting is closed. Results in a moment…</div> : (
          <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{h ? <>Bet on <b>{h.name}</b> to</> : 'Pick a horse, then bet it to'}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'win' | 'place')}><option value="win">win</option><option value="place">place</option></select>
            <input value={amount} placeholder="amount" onChange={(e) => setAmount(e.target.value)} style={{ width: 100 }} aria-label="bet amount" />
            <button className={`btn green ${h && amt >= data.minBet && amt <= me.cash ? '' : 'disabled'}`} onClick={bet}>Place bet</button>
            <span className="small dim grow">
              {h && amt > 0 && <>Returns {money(Math.floor(amt * (kind === 'win' ? h.winOdds : h.placeOdds)))}. </>}
              Up to {money(data.maxBet)} per race at your level{staked > 0 && <> ({money(staked)} bet so far)</>}.
            </span>
          </div>
        )}
        {data.open.myBets.length > 0 && (
          <div style={{ marginTop: 6 }}>
            Your bets on today’s race:{' '}
            {data.open.myBets.map((b: any) => <span key={b.id} className="tag" style={{ marginRight: 4 }}>{money(b.amount)} on {data.open.horses[b.horse].name} to {b.kind}</span>)}
          </div>
        )}
      </div></div>

      {data.history.length > 0 && (
        <Panel title="Past winners">
          {data.history.map((r: any) => (
            <div key={r.postTime} className="feed-item"><span className="grow"><b>{r.winner}</b> <span className="gold">({r.odds})</span>, then {r.second}</span><span className="when">{when(r.postTime)}</span></div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/** Replays the last race from its checkpoints (fractions of the winner's distance). */
function Replay({ race }: { race: any }) {
  const { replay, order } = race.result;
  const steps = replay.length;
  const [t, setT] = useState(steps - 1); // start on the finish; press play to watch it
  const [playing, setPlaying] = useState(false);
  const raf = useRef(0);
  const hooves = useRef<SoundHandle | null>(null);
  const LEN = getSettings().motion === 'off' ? 1500 : 12_000;
  useEffect(() => {
    if (!playing) return;
    const start = performance.now() - (t >= steps - 1 ? 0 : (t / (steps - 1)) * LEN);
    const remaining = (1 - t / (steps - 1)) * LEN;
    hooves.current = play('hooves', { duration: remaining / 1000 });
    const frame = (now: number) => {
      const x = Math.min(steps - 1, ((now - start) / LEN) * (steps - 1));
      setT(x);
      if (x < steps - 1) raf.current = requestAnimationFrame(frame);
      else {
        setPlaying(false);
        play('cheer');
        const won = race.myBets.reduce((s2: number, b: any) => s2 + (b.payout ?? 0), 0);
        if (won > 0) setTimeout(() => { play('chaching'); fx.coins(16); fx.float(`+$${won.toLocaleString('en-US')}`, 'cash'); }, 600);
      }
    };
    raf.current = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf.current); hooves.current?.stop(); };
  }, [playing]);
  const lo = Math.floor(t);
  const hi = Math.min(steps - 1, lo + 1);
  const f = t - lo;
  const done = t >= steps - 1;
  const winnings = race.myBets.reduce((s: number, b: any) => s + (b.payout ?? 0), 0);
  const stakes = race.myBets.reduce((s: number, b: any) => s + b.amount, 0);
  return (
    <Panel title={`Last race · ${when(race.postTime)}`} right={<button className="btn" onClick={() => {
      if (playing) return;
      if (done) setT(0);
      play('bugle');
      setTimeout(() => setPlaying(true), getSettings().motion === 'off' ? 0 : 1500);
    }}>{playing ? 'Running…' : '▶ Watch the race'}</button>}>
      <div className={`race-track-view ${playing ? 'running' : ''}`}>
        {race.horses.map((x: any, i: number) => {
          const pos = replay[lo][i] + (replay[hi][i] - replay[lo][i]) * f;
          const place = order.indexOf(i);
          return (
            <div key={i} className="lane">
              <span className="lane-no">{i + 1}</span>
              <div className="lane-track">
                <div className="runner" style={{ left: `calc(${Math.min(1, pos) * 100}% - ${Math.min(1, pos) * 26}px)` }}>
                  <span className="horse-emoji" aria-hidden="true"><HorseInk /></span>
                  <Silks color={x.silks} size={12} />
                </div>
              </div>
              <span className={`lane-name ${done && place === 0 ? 'gold' : ''}`}>{done ? `${place + 1}. ` : ''}{x.name}</span>
            </div>
          );
        })}
        <div className="finish-line" />
      </div>
      {done && (
        <div style={{ marginTop: 6 }}>
          <b className="gold">{race.horses[order[0]].name}</b> won at {race.horses[order[0]].winLabel}, ahead of {race.horses[order[1]].name} and {race.horses[order[2]].name}.
          {stakes > 0 && <> You bet {money(stakes)} and {winnings > 0 ? <span className="good">collected {money(winnings)}</span> : <span className="bad">lost it</span>}.</>}
        </div>
      )}
    </Panel>
  );
}
